/**
 * MismatchedReservations — Stitch design
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Dialog, DialogContent, DialogActions, TextField, InputAdornment, List, ListItem, ListItemText, Collapse, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import axios from 'axios';
import { BACKEND_URL, API_ENDPOINTS } from '../../utils/apiConfig';
import { toast } from 'react-hot-toast';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const MismatchedReservations = ({ reservationsData, handleDeleteReservation, onReservationClick, setCurrentReservation, setGeselecteerdeDraaidag }) => {
  const [mismatchedReservations, setMismatchedReservations] = useState([]);
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [draaidagSelectDialogOpen, setDraaidagSelectDialogOpen] = useState(false);
  const [availableDraaidagen, setAvailableDraaidagen] = useState([]);
  const [reservationToLink, setReservationToLink] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (Array.isArray(reservationsData)) {
      const mismatched = reservationsData.filter(res => {
        if (res?.fields?.misMatchDataOutlook !== 'True') return false;
        const hasExtraDagNa = res?.fields?.ExtraDagNa === true;
        const hasExtraDagVoor = res?.fields?.ExtraDagVoor === true;
        if (hasExtraDagNa || hasExtraDagVoor) {
          const resStart = new Date(res?.fields?.Start); const resEnd = new Date(res?.fields?.Eind);
          const draaidagStart = new Date(res?.fields?.startDatumDraaidag); const draaidagEnd = new Date(res?.fields?.eindDatumDraaidag);
          let expectedStart = new Date(draaidagStart); let expectedEnd = new Date(draaidagEnd);
          if (hasExtraDagVoor) expectedStart.setDate(expectedStart.getDate() - 1);
          if (hasExtraDagNa) expectedEnd.setDate(expectedEnd.getDate() + 1);
          if (resStart.toDateString() === expectedStart.toDateString() && resEnd.toDateString() === expectedEnd.toDateString()) return false;
        }
        return true;
      });
      setMismatchedReservations(mismatched);
    }
  }, [reservationsData]);

  if (mismatchedReservations.length === 0) return null;

  const initiateDeleteReservation = (reservationId) => { setReservationToDelete(reservationId); setConfirmDeleteDialogOpen(true); };
  const fetchAvailableDraaidagen = async () => { try { const r = await axios.get(`${BACKEND_URL}/Api/availableDraaidagen`); setAvailableDraaidagen(r.data); } catch (e) { console.error("Error fetching available draaidagen:", e); } };

  const handleReservationClick = (reservation) => {
    if (!reservation?.fields?.DraaidagOutlook || reservation.fields.DraaidagOutlook.length === 0) {
      toast.error("Let op! Deze reservering is niet gekoppeld aan een draaidag.");
      setReservationToLink(reservation); fetchAvailableDraaidagen(); setDraaidagSelectDialogOpen(true);
    } else { onReservationClick(reservation); }
  };

  const handleDraaidagSelection = async (selectedDraaidag) => {
    try {
      await axios.patch(API_ENDPOINTS.reservations.update(reservationToLink.id), { fields: { DraaidagOutlook: [selectedDraaidag.id] } });
      setDraaidagSelectDialogOpen(false); setReservationToLink(null);
      toast.success("Reservering succesvol gekoppeld aan de draaidag.");
    } catch (e) { console.error("Error updating reservation:", e); toast.error("Er is een fout opgetreden."); }
  };

  return (
    <>
      {/* Warning banner */}
      <Box sx={{ bgcolor: T.amberBg, border: `1px solid ${T.amber}30`, borderRadius: '0.75rem', p: 2, mt: 2, mb: 2, boxShadow: T.shadow }}>
        <Box onClick={() => setExpanded(!expanded)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}>
          <Icon name="warning" size={20} sx={{ color: T.amber }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 700, color: '#92400e' }}>
              {mismatchedReservations.length} reservering{mismatchedReservations.length !== 1 ? 'en' : ''} met afwijkende data
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: '#92400e' }}>
              Klik om te bekijken en op te lossen
            </Typography>
          </Box>
          <Icon name={expanded ? 'expand_less' : 'expand_more'} size={20} sx={{ color: '#92400e' }} />
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary, mb: 1.5 }}>
              Bij onderstaande reserveringen kloppen de data niet meer met de draaidagdata. Klik op een reservering om de data aan te passen.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {mismatchedReservations.map(r => (
                <Box key={r.id} onClick={() => handleReservationClick(r)} sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  p: 1.5, borderRadius: '0.5rem', bgcolor: T.surface, cursor: 'pointer',
                  transition: 'all 0.15s', '&:hover': { boxShadow: '0 4px 12px rgba(45,51,53,0.06)' },
                }}>
                  <Box>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text }}>
                      {r.fields.reserveringNummer} — {r.fields.Draaidag}
                    </Typography>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.outline }}>
                      Draaidag: {r.fields.startDatumDraaidag} - {r.fields.eindDatumDraaidag}
                    </Typography>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.65rem', color: T.error }}>
                      Reservering: {r.fields.Start} - {r.fields.Eind}
                    </Typography>
                  </Box>
                  <Box onClick={(e) => { e.stopPropagation(); initiateDeleteReservation(r.id); }} sx={{
                    px: 1.5, py: 0.4, borderRadius: '0.5rem', cursor: 'pointer',
                    border: `1.5px solid ${T.error}40`, color: T.error,
                    fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.65rem',
                    transition: 'all 0.15s', '&:hover': { bgcolor: T.errorBg },
                  }}>
                    Verwijder
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDeleteDialogOpen} onClose={() => setConfirmDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '0.75rem' } }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 800, color: T.text }}>Bevestig verwijdering</Typography>
        </Box>
        <DialogContent>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary }}>Weet je zeker dat je deze reservering wilt verwijderen?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box onClick={() => setConfirmDeleteDialogOpen(false)} sx={{ px: 2, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
          <Box onClick={() => { handleDeleteReservation(reservationToDelete); setConfirmDeleteDialogOpen(false); }} sx={{
            px: 2, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer',
            border: `1.5px solid ${T.error}40`, color: T.error,
            fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
            '&:hover': { bgcolor: T.errorBg },
          }}>Verwijderen</Box>
        </DialogActions>
      </Dialog>

      {/* Draaidag select dialog */}
      <Dialog open={draaidagSelectDialogOpen} onClose={() => setDraaidagSelectDialogOpen(false)} PaperProps={{ sx: { borderRadius: '0.75rem' } }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 800, color: T.text }}>Selecteer draaidag</Typography>
        </Box>
        <DialogContent>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, mb: 1.5 }}>Selecteer een draaidag om te koppelen:</Typography>
          <TextField fullWidth placeholder="Zoek draaidag…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Icon name="search" size={18} sx={{ color: T.outlineVariant }} /></InputAdornment> }}
            sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontSize: '0.84rem', bgcolor: T.surfaceLow, fontFamily: T.fontBody, '& fieldset': { borderColor: 'rgba(45,51,53,0.08)' } } }}
          />
          <List>
            {availableDraaidagen.filter(d => d.fields.Draaidag && d.fields.Draaidag.toLowerCase().includes(searchTerm.toLowerCase())).map(draaidag => (
              <ListItem key={draaidag.id} onClick={() => handleDraaidagSelection(draaidag)} sx={{ cursor: 'pointer', borderRadius: '0.35rem', '&:hover': { bgcolor: T.surfaceLow } }}>
                <ListItemText primary={draaidag.fields.Draaidag} secondary={`${draaidag.fields.StartDatum} - ${draaidag.fields.EindDatum}`}
                  primaryTypographyProps={{ sx: { fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 500 } }}
                  secondaryTypographyProps={{ sx: { fontFamily: T.fontBody, fontSize: '0.68rem' } }} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box onClick={() => setDraaidagSelectDialogOpen(false)} sx={{ px: 2, py: 0.6, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MismatchedReservations;
